import {useQuery} from "@tanstack/react-query";

import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import type {ParamsOptions} from "@/src/modules/core/types";

import type {AmenityConfig} from "../lib/amenity-config";
import {buildPropertyAmenitiesListFilter} from "../lib/property-amenities-list-filter";
import {getPropertyAmenityService} from "../lib/registry";
import type {PropertyAmenity} from "../types";

const EMPTY_PROPERTY_AMENITIES: PropertyAmenity[] = [];

type UsePropertyAmenitiesParams = ParamsOptions & {
  config: AmenityConfig | undefined;
  contextPropertyId?: number | null;
  enabled?: boolean;
};

export function usePropertyAmenities({
  config,
  contextPropertyId,
  filter: filterIn,
  enabled,
  ...rest
}: UsePropertyAmenitiesParams) {
  const userProperty = useStoredUserProperty();
  const resolvedPropertyId =
    contextPropertyId !== undefined && contextPropertyId !== null
      ? contextPropertyId
      : userProperty?.id;

  const filter = config
    ? buildPropertyAmenitiesListFilter(config, {
        context_property_id: resolvedPropertyId,
        ...(filterIn ?? {}),
      })
    : {
        context_property_id: resolvedPropertyId,
        ...(filterIn ?? {}),
      };

  const options = {
    sort: "-created_at",
    ...rest,
    filter,
  } as ParamsOptions;

  const isQueryEnabled =
    Boolean(config) && Boolean(resolvedPropertyId) && (enabled ?? true);

  const {data, ...query} = useQuery({
    queryKey: [config?.facilitiesListPath ?? "property-amenities", options],
    queryFn: () => getPropertyAmenityService(config!).getPropertyAmenities(options),
    enabled: isQueryEnabled,
  });

  const propertyAmenities: PropertyAmenity[] =
    query.isSuccess && data ? (data.data as PropertyAmenity[]) : EMPTY_PROPERTY_AMENITIES;

  return {
    propertyAmenities,
    ...query,
  };
}
